import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const fmtNumber = (n) => {
  if (n == null || n === "") return "—";
  if (typeof n === "string") return n;
  return Number(n).toLocaleString();
};

/**
 * StatCard — compact metric card with optional delta, icon, and helper text.
 * Automatically formats numbers with IBM Plex Mono.
 */
export default function StatCard({
  label,
  value,
  helper,
  icon: Icon,
  delta,
  deltaLabel,
  prefix = "",
  suffix = "",
  onClick,
  testId,
  loading,
  className = "",
}) {
  const deltaNum = typeof delta === "number" ? delta : Number(delta);
  const positive = !isNaN(deltaNum) && deltaNum > 0;
  const negative = !isNaN(deltaNum) && deltaNum < 0;
  const DeltaIcon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const Wrapper = onClick ? motion.button : motion.div;

  return (
    <Wrapper
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      onClick={onClick}
      data-testid={testId}
      className={`group relative bg-card border border-border rounded-xl p-5 text-left card-hover w-full ${className}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </div>
        {Icon && (
          <div className="w-7 h-7 rounded-md bg-secondary flex items-center justify-center text-muted-foreground group-hover:text-foreground transition-colors">
            <Icon size={14} strokeWidth={1.75} />
          </div>
        )}
      </div>
      {loading ? (
        <div className="skeleton h-8 w-24 mb-2" />
      ) : (
        <div className="font-mono font-semibold text-2xl md:text-3xl tracking-tight text-foreground leading-tight">
          {prefix}
          {fmtNumber(value)}
          {suffix}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2 min-h-[18px]">
        {!loading && !isNaN(deltaNum) && delta != null && (
          <div
            className={`flex items-center gap-1 text-xs font-mono font-medium ${
              positive ? "text-[hsl(var(--success))]" : negative ? "text-destructive" : "text-muted-foreground"
            }`}
          >
            <DeltaIcon size={12} />
            {positive ? "+" : ""}
            {deltaNum}
            {deltaLabel ? ` ${deltaLabel}` : "%"}
          </div>
        )}
        {helper && <div className="text-xs text-muted-foreground truncate">{helper}</div>}
      </div>
    </Wrapper>
  );
}
